import React, { useEffect, useState, useRef } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import ServicesMegaMenu from "./ServicesMegaMenu";

const links = [
  { to: "/the-house", label: "The House of Impact" },
  { to: "/manifesto", label: "Manifesto" },
  { to: "/expert-minds", label: "Expert Minds" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const closeTimer = useRef(null);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const overHero = isHome && !scrolled && !megaOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMegaOpen(false);
  }, [location.pathname]);

  const textColor = overHero ? "#F4EFE7" : "#14140F";
  const bronzeColor = "#8C6B3F";
  const textShadow = overHero ? "0 1px 16px rgba(0,0,0,0.7)" : "none";

  const openMega = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setMegaOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setMegaOpen(false), 500);
  };

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled || megaOpen ? "backdrop-blur-md bg-[#F4EFE7]/95" : "bg-transparent"
        }`}
        style={{
          borderBottom: "1px solid transparent",
          color: textColor,
          textShadow,
        }}
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-2 flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-logo-link"
            className="flex items-center gap-3 group"
          >
            <div
              className="overflow-hidden"
              style={{
                width: "clamp(180px, 20vw, 360px)",
                aspectRatio: "1603 / 109",
              }}
            >
              <img
                src="https://customer-assets-jt897jd0.emergentagent.net/job_luxury-institute/artifacts/j0je1nsj_NIL-Gold1.png"
                alt="Next in Luxury"
                className="block transition-opacity duration-300"
                style={{
                  width: "124.14%",
                  maxWidth: "none",
                  height: "auto",
                  marginLeft: "-13.41%",
                  marginTop: "-41.55%",
                  filter: overHero
                    ? "drop-shadow(0 2px 18px rgba(0,0,0,0.6))"
                    : "none",
                }}
              />
            </div>
          </Link>

          {/* Desktop */}
          <nav className="hidden lg:flex items-center gap-10">
            {links.slice(0, 2).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `nav-link font-eyebrow text-[11px] ${isActive ? "active" : ""}`
                }
                style={{ color: textColor }}
              >
                {l.label}
              </NavLink>
            ))}

            {/* Services — mega menu trigger (hover-safe wrapper with extended bottom zone) */}
            <div
              className="relative"
              onMouseEnter={openMega}
              onMouseLeave={scheduleClose}
              data-testid="nav-services-trigger"
              style={{ paddingBottom: "1rem", marginBottom: "-1rem" }}
            >
              <NavLink
                to="/services"
                className={({ isActive }) =>
                  `nav-link font-eyebrow text-[11px] block py-6 ${
                    isActive || megaOpen ? "active" : ""
                  }`
                }
                style={{ color: textColor }}
              >
                Services
              </NavLink>
            </div>

            {links.slice(2).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `nav-link font-eyebrow text-[11px] ${isActive ? "active" : ""}`
                }
                style={{ color: textColor }}
              >
                {l.label}
              </NavLink>
            ))}

            <Link
              to="/private-inquiry"
              data-testid="nav-inquiry-cta"
              className="inquiry-cta font-eyebrow text-[11px]"
              style={{ color: bronzeColor, borderColor: bronzeColor }}
            >
              Private Inquiry
            </Link>
          </nav>

          {/* Mobile trigger */}
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden font-eyebrow text-[11px]"
            style={{ color: textColor }}
            aria-label="Menu"
          >
            {open ? "Close" : "Menu"}
          </button>
        </div>

        {/* Mega menu panel — outside the flex row, spans full nav width */}
        <ServicesMegaMenu
          open={megaOpen}
          onOpen={openMega}
          onClose={scheduleClose}
        />
      </header>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="nav-mobile-drawer"
          className="fixed inset-0 z-40 lg:hidden"
          style={{ background: "#F4EFE7" }}
        >
          <div className="pt-28 px-8 flex flex-col gap-8 overflow-y-auto h-full pb-12">
            {[...links.slice(0, 2), { to: "/services", label: "Services" }, ...links.slice(2)].map((l) => (
              <React.Fragment key={l.to}>
                <NavLink
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.to.replace("/", "")}`}
                  className="title-caps text-3xl"
                >
                  {l.label}
                </NavLink>
                {l.to === "/services" && (
                  <div className="pl-6 flex flex-col gap-4 -mt-4">
                    <NavLink to="/services/consulting" className="font-script text-3xl" style={{ color: bronzeColor }}>
                      — Consulting
                    </NavLink>
                    <NavLink to="/services/training" className="font-script text-3xl" style={{ color: bronzeColor }}>
                      — Training
                    </NavLink>
                  </div>
                )}
              </React.Fragment>
            ))}
            <Link
              to="/private-inquiry"
              data-testid="nav-mobile-inquiry"
              className="inquiry-cta font-eyebrow text-[13px] mt-4 self-start"
              style={{ color: bronzeColor, borderColor: bronzeColor }}
            >
              Private Inquiry
            </Link>
          </div>
        </div>
      )}
    </>
  );
}
